import { useTranslation } from 'react-i18next'
import { type Options as MarkdownOptions } from 'react-markdown'
import {
  data,
  type LoaderFunctionArgs,
  type MetaFunction,
  useLoaderData,
} from 'react-router'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Markdown } from '#app/components/markdown.tsx'
import { getInstance, getLocale } from '#app/middleware/i18next.ts'
import { type ApplicationContent } from '#app/types/application-content.ts'
import { executeQuery } from '#app/utils/cms.server.ts'
import { siteTitle } from '#app/utils/global.ts'
import { groupedByContentType } from '#app/utils/grouped-content.ts'
import { getLocalizedContent } from '#app/utils/i18n.ts'
import { Status } from '#app/utils/status.ts'

enum PrivacyContent {
  TITLE = 'Privacy Policy Title',
  CONTENT = 'Privacy Policy Content',
}

export async function loader({ context }: LoaderFunctionArgs) {
  const locale = getLocale(context)
  const i18next = getInstance(context)
  const t = await i18next.getFixedT(locale)

  const errorData = {
    title: t('errors.general.title'),
    description: t('errors.general.description'),
  }

  const pageTitle = t('privacyPolicy', locale)

  const contentQuery = `*[_type == 'applicationContent' && contentType match 'Privacy Policy*']`
  const contentResult = await executeQuery(contentQuery)

  if (contentResult.status !== Status.Success) {
    throw data(errorData, { status: 500 })
  }

  const contentData = contentResult.data as ApplicationContent[]

  return {
    pageTitle,
    content: groupedByContentType(contentData),
  }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const pageTitle = data?.pageTitle ?? 'Error'
  return [{ title: `${pageTitle} | ${siteTitle}` }]
}

export default function PrivacyPolicyRoute() {
  const { content } = useLoaderData<typeof loader>()

  const { i18n } = useTranslation()
  const locale = i18n.language

  const markdownOptions: MarkdownOptions = {
    components: {
      strong(props) {
        const { children } = props
        return <strong className="font-bold!">{children}</strong>
      },
    },
  }

  return (
    <main id="main-content">
      <div className="container mt-6">
        <h1 className="text-6 text-cyan-12 font-bold">
          {getLocalizedContent(content[PrivacyContent.TITLE]?.content, locale)}
        </h1>
        <div className="mt-8">
          <Markdown
            options={markdownOptions}
            text={getLocalizedContent(
              content[PrivacyContent.CONTENT]?.content,
              locale,
            )}
          />
        </div>
      </div>
    </main>
  )
}

export function ErrorBoundary() {
  return (
    <GeneralErrorBoundary
      statusHandlers={{
        500: ({ error }) => (
          <div className="text-center">
            <h1>{error.data.title}</h1>
            <p className="text-3 mt-2 font-normal">{error.data.description}</p>
          </div>
        ),
      }}
    />
  )
}
